import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashValues from 'lodash/fp/values';

import analytics from 'client/analytics';
import { ANALYTICS_EVENT_TEXT_AGENT_BUTTON_CLICK } from 'client/analytics/eventList';

import { SCREEN_ID_FAQ_APPLICATION } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import branch from 'client/core/branch';
import MarketActions from 'client/actions/Market';

import SrBox from 'client/components/SrBox';
import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';

@withTransitionHook
@withScreenId(SCREEN_ID_FAQ_APPLICATION)
@withScrollToTop
@branch({
  marketsById: ['markets', 'byId'],
})
class FaqApplication extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'FAQ Application | Sunroom Rentals',
    section: 'FAQ Application',
    // canonical: '/',
    keywords: 'rent, home, austin, tour, apartment, sunroom faq',
    description: (
      'Common Application Questions From Renters: ' +
      'How long is the application process? ' +
      'When should I start touring & applying?'
    ),
  })

  static propTypes = {
    marketsById: PropTypes.object.isRequired,
  }

  static fetchData(match, inject) {
    return Promise.all([
      MarketActions.getList(inject),
    ]);
  }

  _onTextExpertButtonClick = () => {
    analytics.track(ANALYTICS_EVENT_TEXT_AGENT_BUTTON_CLICK);
  }

  render() {
    const {
      marketsById,
    } = this.props;

    const marketsList = lodashValues(marketsById);

    return (
      <div className='page-faq'>
        <Navbar
          leftButton={(
            <SrLink restoreScrollPosition redirectToLastLocation to='/feed'>
              Back
            </SrLink>
          )}
        />

        <div className='faq'>
          <span className='headline'>Common Application Questions From Renters</span>

          <div className='questionsContainer'>
            <span className='question'>How long is the application process?</span>
            <span className='answer'>
              Each application typically takes
              <span className='italics'> 3-5 business days </span>
              to process and be approved.
              Our team works to qualify your application,
              and then it's sent to the home's Landlord for final approval.
              <SrBox marginTop='10px' />
              The time for processing can vary, depending on how thorough
              the information on the application is, how quickly applicants
              respond to additional questions, and other factors.
              <SrBox marginTop='10px' />
              <span className='italics'>Most</span> homes will operate on a "first-qualified" basis,
              meaning we will first process the first application submitted.
              If that application qualifies for the home and is approved by the Landlord,
              the home will be leased to that first applicant.
              <SrBox marginTop='10px' />
              If the first application does not meet the qualifications or drops out,
              the second application will be processed, and so on. With "first qualified" homes,
              <span className='italics'>each application can take 3-5 business days to be processed</span>.
              <SrBox marginTop='10px' />
              A small number of homes will operate on a "best qualified" basis,
              meaning that we will process all applicaitons, and send them to the Landlord to approve
              what they deem is the best qualified application.
            </span>
            <span className='question'>When should I start touring & applying?</span>
            <span className='answer'>
              We suggest that you tour homes about 3-6 weeks before you'd like to move in & that
              you apply 2-3 weeks before you’re ready to start paying rent.
              <SrBox marginTop='10px' />
              Most homes are fairly strict on the move-in dates - often a home needs to be cleaned,
              or repairs made, before it's ready for move-in.
              If you plan to move in after the home's stated move-in window, you should still expect
              to start paying rent during the home's move-in window.
            </span>
            <span className='question'>Will the landlord accept my move-in date?</span>
            <span className='answer'>
              We understand you want to plan ahead to start looking early!
              Rentals go quickly & landlords are looking for renters who are ready to start paying rent now.
              So, expect that rentals will not be held for more than 2 weeks after they become vacant.
            </span>
            <span className='question'>Who is looking at my application?</span>
            <span className='answer'>
              First, we review your application - we will ask for more information where needed,
              and will help you fix any errors.
              Then, your application is submitted & the property owner will give approval.
            </span>
            <span className='question'>How do I prove my income?</span>
            <span className='answer'>
              The most standard documentation to provide is your pay stubs from a current/previous employer.
              If you're self-employed, just started a new job, not working, retired or do not receive
              consistent paychecks, you can submit bank statements showing income or regular deposits, or an
              offer letter or other letter from your employer stating income.
            </span>
            <span className='question'>Will the owner accept my pets?</span>
            <span className='answer'>
              Lots of homes will allow pets!  Pet policies vary by property & while
              in general are fairly set for each home, they can sometimes be flexible.
              If you have questions about fees, deposits, or breed restrictions let our Support team know.
            </span>
            <span className='question'>What if I have an ESA or Support Animal?</span>
            <span className='answer'>
              We’re experts at handling any pet situation. If you have an ESA or Service Animal,
              please provide valid documentation when you apply, as Service Animals and ESAs that
              are verified will be accepted, no matter what the home's pet policies.  When you have questions,
              bring us into the loop so we can help you!
            </span>
            <span className='question'>Should I apply to multiple properties?</span>
            <span className='answer'>
              Yes,
              <span className='bold color-green'> we recommend applying to multiple properties </span>
              that you like, so that you have the best chance of landing one of your favorites.
            </span>
            <span className='question'>How do I pay my application fees?</span>
            <span className='answer'>
              With Sunroom, you pay your application fees online! The fees cover the cost of credit
              & background checks that landlords require, and Sunroom does not make a profit on these fees.
              Please note that once your credit and background checks have been run, the application fees
              are non-refundable.
            </span>
            <span className='question'>Ready to Apply?</span>
            <span className='answer'>
              <ol>
                <li>
                  Go ahead and complete your application, and invite your roommates, spouse, partner, etc.
                  Everyone (over the age of 18) living at the property needs to complete an application.
                  If you will be using a Guarantor to apply, be sure to invite them to apply as well.
                  A Guarantor is needed when any applicants don't meet the home's credit and/or income
                  requirements. The Guarantor must meet the home's requirements and will co-sign the lease,
                  but not live in the home.
                </li>
                <li>
                  Have your info ready. Government ID, Proof of income,
                  Pet photos, Employment history, and Rental history.
                </li>
                <li>
                  {
                    'Start your rental application online. It takes 30-minutes and is easiest ' +
                    'to complete on your laptop.'
                  }
                </li>
              </ol>
            </span>
            <span className='question'>What cities does Sunroom support?</span>
            <span className='answer'>
              Currently we support: {marketsList.map(({ displayName }) => displayName).join(', ')}.
            </span>
          </div>
        </div>

        <div className='mainContainer'>

          <span className='headline'>Text us questions</span>

          <div className='teamPhoto' />

          <span className='bodyText'>
            If you have questions about a home or apartment, send us a text.
            We can call the landlord on your behalf, schedule tours, process your
            application and also provide general rental hunting tips.
            <br />
            <br />
            Feel free to link me rentals you find on other sites. We'll make sure
            you get to tour the home whenever you want!
          </span>
          <a href='sms:+15127143550' onClick={this._onTextExpertButtonClick} className='button blue'>
            <span className='prompt'>text </span>
            <span className='normal'>(512) 714-3550</span>
          </a>
        </div>
      </div>
    );
  }
}

FaqApplication.displayName = 'FaqApplication';

export default FaqApplication;
